import React from 'react'
import classnames from 'classnames'
import { formatCurrency } from '../../../../util/string'
import { TenantKpiRollup } from './accountStatusUtils'
import PortaledTooltip from './PortaledTooltip'
import StatusChipTooltipContent from './StatusChipTooltipContent'

export interface TenantKpiBarProps {
  kpis: TenantKpiRollup
}

interface KpiItemProps {
  label: string
  value: React.ReactNode
  tone: 'neutral' | 'green' | 'red' | 'yellow'
  description: string
}

const toneClass: Record<KpiItemProps['tone'], string> = {
  neutral: 'text-slate-900 dark:text-slate-100',
  green: 'text-green-700 dark:text-green-400',
  red: 'text-red-700 dark:text-red-400',
  yellow: 'text-amber-700 dark:text-amber-400',
}

const KpiItem: React.FC<KpiItemProps> = ({ label, value, tone, description }) => (
  <PortaledTooltip
    className="min-w-[7rem] flex-1 text-center"
    content={<StatusChipTooltipContent label={label} description={description} />}
  >
    <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 sm:text-xs">
      {label}
    </div>
    <div className={classnames('mt-1 text-sm font-semibold sm:text-base', toneClass[tone])}>{value}</div>
  </PortaledTooltip>
)

function balanceKpiDescription(base: string, excludedCredit: number): string {
  if (excludedCredit > 0) {
    return `${base} Credit (negative) balances count as $0; ${formatCurrency(excludedCredit)} in credits excluded.`
  }
  return `${base} Credit (negative) balances count as $0.`
}

export const TenantKpiBar: React.FC<TenantKpiBarProps> = ({ kpis }) => {
  const {
    totalBalance,
    totalDue,
    excludedCreditTotal,
    excludedCreditDue,
    activeAccounts,
    totalAccounts,
    activeServiceAgreements,
    totalServiceAgreements,
    optiRateEnrolled,
    optiRateTotal,
    statusesLoaded,
  } = kpis

  const balanceTone: KpiItemProps['tone'] = totalBalance > 0 ? 'red' : 'green'
  const dueTone: KpiItemProps['tone'] = totalDue > 0 ? 'red' : 'green'

  return (
    <div className="mt-6 flex flex-wrap items-stretch gap-2 rounded-lg border border-slate-200 bg-white px-3 py-3 dark:border-slate-700 dark:bg-slate-800 sm:gap-4 sm:px-4">
      <KpiItem
        label="Balance Total"
        value={formatCurrency(totalBalance)}
        tone={balanceTone}
        description={balanceKpiDescription(
          'Sum of billed balance (Balance Total) across all tenant accounts.',
          excludedCreditTotal
        )}
      />
      <KpiItem
        label="Balance Due"
        value={formatCurrency(totalDue)}
        tone={dueTone}
        description={balanceKpiDescription(
          'Sum of currently due (Balance Due) across all tenant accounts.',
          excludedCreditDue
        )}
      />
      <KpiItem
        label="Active Accounts"
        value={statusesLoaded ? `${activeAccounts}/${totalAccounts}` : '—'}
        tone="neutral"
        description="Accounts with Account Status ACTIVE / total accounts"
      />
      <KpiItem
        label="Active SAs"
        value={`${activeServiceAgreements}/${totalServiceAgreements}`}
        tone="neutral"
        description="Active service agreements / all service agreements"
      />
      <KpiItem
        label="OptiRate Enrollment"
        value={`${optiRateEnrolled}/${optiRateTotal}`}
        tone="neutral"
        description="Accounts with any ENROLLED OptiRate SA / total accounts"
      />
    </div>
  )
}

export default TenantKpiBar
